import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";

import { Button, InputText, Text } from "@pentabd/ui";
import {
  CancelButton,
  ErrorModal,
  InputDateControllerRow,
  InputTextAreaRow,
  InputTextRow,
  MultiFileRow,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import useActionCreator from "@redux/hooks/useActionCreator";

import {
  PROCUREMENT_VALIDATION,
  ProcurementValidationSchemaType,
} from "@validations/Inventory/procurement/procurement";

import useCustomSelector from "@redux/hooks/useCustomSelector";

import { ROUTES } from "@constants/routes";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

const INVENTORY_TYPES = [
  {
    label: "ICT",
    value: "ICT",
  },
  {
    label: "NON_ICT",
    value: "NON_ICT",
  },
  {
    label: "BOTH",
    value: "BOTH",
  },
];

interface CreateProcurementsFormProps {
  procurement: any;
  detailsView?: boolean;
  procurementType: string;
}

const CreateProcurementsForm = ({
  procurement,
  detailsView,
  procurementType,
}: CreateProcurementsFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteSuccess,
    deleteError,
  } = useCustomSelector((store) => store.inventory.procurementsState);
  const { createProcurement } = useActionCreator();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const method = useForm<ProcurementValidationSchemaType>({
    // resolver: yupResolver(procurementValidationSchema),
    values: revertMultipleDocumentData(procurement, [
      PROCUREMENT_VALIDATION.DOCUMENT,
    ]),
  });

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = method;

  const createSuccessAction = () => {
    navigate(`${ROUTES.INVENTORY.PROCUREMENTS}`);
  };
  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onSubmit: SubmitHandler<ProcurementValidationSchemaType> = (
    data: ProcurementValidationSchemaType,
  ) => {
    const convertedData = convertMultipleDocumentData(data, [
      PROCUREMENT_VALIDATION.DOCUMENT,
    ]);

    const _convertedData = {
      ...convertedData,
      procurementType,
    };

    createProcurement(_convertedData as any);
  };

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case updateSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case deleteSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!updateError: {
        setIsErrorModalOpen(true);
        break;
      }
      case !!deleteError: {
        setIsErrorModalOpen(true);
        break;
      }
      default:
        break;
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteError,
    deleteSuccess,
  ]);

  return (
    <div className="pt-10">
      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      )}
      {/* modal end */}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="d-flex flex-column gap-6"
      >
        <div className="row">
          <div className="col-lg-3">
            <Text className="text-title fs-sm lh-sm fw-semibold">
              {t(
                "CREATE_PROCUREMENTS.FORM.FORM_FIELDS.NAME_OF_PROCUREMENT.NAME",
              )}
            </Text>
          </div>
          <div className="col-lg-7">
            <div className="row gy-0">
              <div className="col-6">
                <InputText
                  {...register(PROCUREMENT_VALIDATION.NAME_EN)}
                  placeholder={t(
                    "CREATE_PROCUREMENTS.FORM.FORM_FIELDS.NAME_OF_PROCUREMENT.PLACEHOLDER_1",
                  )}
                  error={errors as any}
                  disabled={detailsView}
                />
              </div>
              <div className="col-6">
                <InputText
                  {...register(PROCUREMENT_VALIDATION.NAME_BN)}
                  placeholder={t(
                    "CREATE_PROCUREMENTS.FORM.FORM_FIELDS.NAME_OF_PROCUREMENT.PLACEHOLDER_2",
                  )}
                  error={errors as any}
                  disabled={detailsView}
                />
              </div>
            </div>
          </div>
        </div>

        <InputTextRow
          label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.REF_NO"
          register={{ ...register(PROCUREMENT_VALIDATION.REF_NO) }}
          disabled={detailsView}
        />

        <InputTextAreaRow
          label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.DETAILS"
          register={{ ...register(PROCUREMENT_VALIDATION.DETAILS) }}
          disabled={detailsView}
        />

        <InputTextRow
          label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.REMARKS"
          register={{ ...register(PROCUREMENT_VALIDATION.REMARKS) }}
          disabled={detailsView}
        />

        <InputDateControllerRow
          label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.DATE"
          control={control}
          register={{ ...register(PROCUREMENT_VALIDATION.DATE) }}
          name={PROCUREMENT_VALIDATION.DATE}
          disabled={detailsView}
        />

        <SelectControllerRow
          label="CREATE_PROCUREMENTS.FORM.FORM_FIELDS.INVENTORY_TYPE"
          control={control}
          name={PROCUREMENT_VALIDATION.INVENTORY_TYPE}
          register={{
            ...register(PROCUREMENT_VALIDATION.INVENTORY_TYPE, {
              required: "Inventory Type is required",
            }),
          }}
          options={INVENTORY_TYPES}
          setValue={setValue}
          error={errors}
          required
          disabled={detailsView}
        />

        <MultiFileRow
          control={control}
          name={PROCUREMENT_VALIDATION.DOCUMENT}
          register={register}
          setValue={setValue}
          fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          disabled={detailsView}
        />

        {!detailsView && (
          <div className="text-end border-top pt-10">
            <CancelButton />

            <Button
              fill="fill"
              type="primary"
              size="sm"
              htmlType="submit"
              className="ms-5"
            >
              {t("CREATE_PROCUREMENTS.FORM.BUTTON_SAVE.BUTTON_TEXT")}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateProcurementsForm;
